import type { Theme } from '@emotion/react';
import { css } from '@emotion/react';
import { hideVisually } from 'polished';

import { breakpoint, colorUtils, duration, easing } from '@twistopayments/design-system/styles';

export const root = (theme: Theme) => css`
  padding: ${theme.spacing[0]};
  border-radius: ${theme.borderRadius.sm};
  border: none;
  color: ${theme.palette.text.body};
  background: transparent;
  ${theme.presentation.choice.frame}
`;

export const checkboxIcon = (theme: Theme) => css`
  margin-right: ${theme.presentation.choice.iconGap};
`;

export const content = (theme: Theme, size: 'medium' | 'small') => css`
  display: flex;
  flex-direction: column;
  gap: ${size === 'small' ? theme.spacing[4] : theme.spacing[8]};
  min-width: 0;

  ${breakpoint.up('sm')} {
    gap: ${theme.spacing[8]};
  }
  ${theme.presentation.choice.content}
`;

export const input = (theme: Theme) => css`
  ${hideVisually()}
  & + div {
    transition: box-shadow ${duration.short}ms ${easing.easeInOut};
    outline: none;
  }

  &:focus + div,
  &:focus:hover + div {
    box-shadow: 0 0 0
      ${`${theme.spacing[4]} ${colorUtils.colorWithOpacity(theme.palette.ghost.lowContrast, 0.24)}`};
  }

  &:hover + div {
    box-shadow: none;
  }
  ${theme.presentation.choice.interaction}

  &:disabled + div {
    box-shadow: none;
  }
`;

export const inputError = (theme: Theme) => css`
  &:focus + div {
    border-color: ${theme.palette.error.main};
  }
`;

export const formHelperText = (
  theme: Theme,
  size: 'medium' | 'small',
  hasError: boolean,
  disabled: boolean
) => css`
  margin-top: ${theme.spacing[0]};

  && {
    color: ${(disabled && theme.presentation.choice.disabledHelperColor) ||
    (hasError ? theme.palette.error.main : theme.presentation.choice.helperColor)};
  }

  p {
    ${size === 'small'
      ? typographyStyles(theme.typography.app.overlineMobile)
      : typographyStyles(theme.typography.app.captionMobile)}
  }

  ${breakpoint.up('sm')} {
    p {
      ${size === 'small'
        ? typographyStyles(theme.typography.app.overline)
        : typographyStyles(theme.typography.app.caption)}
    }
  }
  ${theme.presentation.choice.helper}
`;

export const label = (theme: Theme) => css`
  display: flex;
  align-items: ${theme.presentation.choice.align};
`;

export const endAdornment = (theme: Theme) => css`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;
  padding-left: ${theme.spacing[12]};
`;

const typographyStyles = (typography: {
  fontSize: number;
  lineHeight: number | 'auto';
  letterSpacing: number;
}) => css`
  font-size: ${typography.fontSize}px;
  line-height: ${typography.lineHeight === 'auto' ? 'normal' : `${typography.lineHeight}px`};
  letter-spacing: ${typography.letterSpacing}px;
`;

export const defaultLabelText = (theme: Theme) => css`
  ${typographyStyles(theme.typography.app.bodySmMobile)}

  ${breakpoint.up('sm')} {
    ${typographyStyles(theme.typography.app.bodySm)}
  }
  ${theme.presentation.choice.label}
`;

export const smallerLabelText = (theme: Theme) => css`
  ${typographyStyles(theme.typography.app.captionMobile)}

  ${breakpoint.up('sm')} {
    ${typographyStyles(theme.typography.app.caption)}
  }
  ${theme.presentation.choice.smallLabel}
`;
